import { app } from '../../env'
import { USER_JOURNEY_APPLICATION_NAME, USER_JOURNEY_PLATFORM_NAME } from './constants'
import { JourneyOutcome, JourneyTerminationReason, UserJourney } from './types'

/**
 * Field names and shapes mirror the Cash `RecordCompletedJourneyRequest` proto
 * (`squareup.cash.blockuserjourneys.api.v1`) exactly, so swapping the Datadog sink for a
 * POST to the real UJ service is payload-identical.
 */
export interface RecordCompletedJourneyPayload {
  journey_name: string
  journey_id: string
  entity_type: 'ENTITY_TYPE_UNSPECIFIED'
  app_token: 'none'
  platform_name: string
  platform_version: string
  application_name: string
  application_version: string
  locale: string
  created_at: number
  ended_at: number
  duration_real_time_ms: number
  outcome: JourneyOutcome
  termination_reason?: JourneyTerminationReason
  variant?: string
  frustration_signals: string[]
  friction_signals: string[]
  tags: string[]
  session_id: string
  is_retry: boolean
}

/**
 * Emitted at journey start (not part of the Cash contract, which is completion-only).
 * Deliberate addition: started events give Datadog the denominator to derive abandonment
 * analytically (started − completed per session).
 */
export interface JourneyStartedEvent {
  journey_name: string
  journey_id: string
  variant?: string
  session_id: string
  application_name: string
  application_version: string
  platform_name: string
  created_at: number
}

const browserVersion = (): string => {
  if (typeof navigator === 'undefined') {
    return ''
  }
  // Edge UAs carry "Chrome/x ... Edg/x", so the Edge token must be checked before the
  // leftmost-match general pattern (which would always land on Chrome's version)
  const edge = navigator.userAgent.match(/Edg(?:A|iOS)?\/(\d+[\d.]*)/)
  if (edge !== null) {
    return edge[1]
  }
  // Chrome/Firefox (incl. iOS variants) match their own token; Safari carries "Version/x"
  const match = navigator.userAgent.match(/(?:CriOS|Chrome|FxiOS|Firefox|Version)\/(\d+[\d.]*)/)
  return match?.[1] ?? ''
}

export const buildJourneyStartedEvent = (journey: UserJourney, esid: string): JourneyStartedEvent => ({
  journey_name: journey.name,
  journey_id: journey.id,
  variant: journey.variant,
  session_id: esid,
  application_name: USER_JOURNEY_APPLICATION_NAME,
  application_version: app.APP_VERSION,
  platform_name: USER_JOURNEY_PLATFORM_NAME,
  created_at: journey.created,
})

export const buildRecordCompletedJourneyPayload = (
  journey: UserJourney,
  outcome: JourneyOutcome,
  terminationReason: JourneyTerminationReason | undefined,
  esid: string
): RecordCompletedJourneyPayload => {
  const endedAt = Date.now()

  return {
    journey_name: journey.name,
    journey_id: journey.id,
    entity_type: 'ENTITY_TYPE_UNSPECIFIED',
    app_token: 'none',
    platform_name: USER_JOURNEY_PLATFORM_NAME,
    platform_version: browserVersion(),
    application_name: USER_JOURNEY_APPLICATION_NAME,
    application_version: app.APP_VERSION,
    locale: typeof navigator === 'undefined' ? '' : navigator.language,
    created_at: journey.created,
    ended_at: endedAt,
    duration_real_time_ms: endedAt - journey.created,
    outcome,
    termination_reason: terminationReason,
    variant: journey.variant,
    frustration_signals: journey.frustrations,
    friction_signals: journey.frictions,
    tags: journey.tags,
    session_id: esid,
    is_retry: false,
  }
}
